/* KBW — the gallery thumbnail rail's scroll buttons.

   One file for all six screens that draw a rail (cabinet, vanity, flooring,
   sink and stone product pages, and the styles screen), loaded globally from
   layout/theme.liquid next to kbw-frame.css, which owns the rail's CSS.

   Its main job: the chevron above and the chevron below the rail. The
   thumbnails themselves — which picture is showing, which thumb is marked —
   belong to kbw-pdp.js and the styles screen's own script, and this file does
   not touch them.

   Why the buttons are revealed from here rather than written by Liquid: the
   question is "do the thumbnails fit", and the answer depends on the rail's
   rendered height, which Liquid cannot know. Counting images in Liquid would
   mean hard-coding how many fit and re-deriving it every time the rail's width
   or the thumbnail size changes. So the markup ships the buttons hidden and
   this measures. With JavaScript off they stay hidden and the rail still
   scrolls natively — nothing is lost, only the shortcut.

   The rail runs two ways. From 1400px it is a column and the two buttons are
   up and down; from 1024 to 1399px it is a horizontal strip and the same two
   buttons are left and right. Which one it is is read off the rail's
   `flex-direction`, so this file follows the stylesheet instead of keeping its
   own copy of the breakpoint. Below 1024px CSS keeps the buttons display:none
   and the strip is scrolled by finger.

   A second, unrelated job lives at the bottom of this file because it is the
   same kind of thing — a few lines that every product page needs and no
   section should print for itself: it keeps `--kbw-sticky-top` equal to the
   bottom edge of the header, which the sticky gallery column reads. */

(function () {
  'use strict';

  var RAILS = '.kbw-pdp__rail, .kbw-sh__thumbs';
  var THUMBS = '.kbw-pdp__thumb, .kbw-sh__thumb';

  /* The gap between thumbnails, kept in step with kbw-frame.css. Only used as
     a fallback when the rail is empty and there is no thumbnail to measure. */
  var GAP = 9;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setup(rail) {
    /* `shopify:section:load` fires again when an editor drops the section back
       in. Without this guard every reload would stack another set of click
       handlers and one tap would scroll several steps. */
    if (rail.dataset.kbwRailReady === '1') return;
    rail.dataset.kbwRailReady = '1';

    var scope = rail.parentNode;
    if (!scope) return;

    var up = scope.querySelector('.kbw-railbtn--up');
    var down = scope.querySelector('.kbw-railbtn--down');
    if (!up || !down) return;

    /* The buttons are named for the vertical rail ("up" / "down" in the class
       and the markup); on the horizontal strip they are the previous and the
       next. The class stays, the reading changes. */
    function isRow() {
      return window.getComputedStyle(rail).flexDirection.indexOf('row') === 0;
    }

    /* One thumbnail plus one gap. Read off the page rather than assumed, so a
       change to the thumbnail size in CSS does not silently desynchronise the
       scroll step from what the customer sees. */
    function step() {
      var row = isRow();
      var thumb = rail.querySelector(THUMBS);
      if (!thumb) return (row ? rail.clientWidth : rail.clientHeight) || 80;
      var box = thumb.getBoundingClientRect();
      return Math.round(row ? box.width : box.height) + GAP;
    }

    function scrollBy(delta) {
      var opts = { behavior: prefersReducedMotion() ? 'auto' : 'smooth' };
      if (isRow()) opts.left = delta; else opts.top = delta;
      rail.scrollBy(opts);
    }

    function label(button, text) {
      if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
    }

    function sync() {
      var row = isRow();

      label(up, row ? 'Scroll thumbnails left' : 'Scroll thumbnails up');
      label(down, row ? 'Scroll thumbnails right' : 'Scroll thumbnails down');

      /* 1px of slack: sub-pixel layout can leave scrollHeight a hair above
         clientHeight on a rail that visibly fits, which would show a pair of
         chevrons that cannot move anything. */
      var size = row ? rail.clientWidth : rail.clientHeight;
      var extent = row ? rail.scrollWidth : rail.scrollHeight;
      var overflows = extent - size > 1;

      up.hidden = !overflows;
      down.hidden = !overflows;
      if (!overflows) return;

      var pos = row ? rail.scrollLeft : rail.scrollTop;
      up.disabled = pos <= 0;
      down.disabled = pos + size >= extent - 1;
    }

    up.addEventListener('click', function () { scrollBy(-step()); });
    down.addEventListener('click', function () { scrollBy(step()); });

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    /* The rail's size changes when the breakpoint flips it from a row to a
       column, and thumbnails are lazy-loaded, so the first measurement can be
       taken before the pictures have given the rail its real size. */
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(sync).observe(rail);
    }

    sync();
  }

  function init() {
    var rails = document.querySelectorAll(RAILS);
    for (var i = 0; i < rails.length; i++) setup(rails[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* --- Sticky offset for the gallery column ----------------------------------
     The header hides on the way down and slides back on the way up
     ("on-scroll-up"), so a fixed `top` would leave a header-high band of empty
     page above the gallery for the whole descent. The same trick as the filter
     rail (kbw-filter-rail.js): publish where the header's bottom edge is now.

     Scroll events stop before the header's slide finishes, so a single read on
     the last event would freeze the variable half way through the animation.
     After every scroll the offset is therefore re-read each frame for a short
     while, which covers the slide.

     A sticky announcement bar stays on screen when the header leaves, so its
     bottom edge counts too. Without this script the stylesheet falls back to a
     measured header height (kbw-frame.css). */
  var SETTLE_MS = 600;
  var settleUntil = 0;
  var looping = false;
  var lastTop = -1;

  function readOffset() {
    var bottom = 0;
    var header = document.querySelector('.section-header');
    if (header) bottom = header.getBoundingClientRect().bottom;
    var bar = document.getElementById('x-announcement');
    if (bar && bar.dataset.isSticky === 'true') {
      bottom = Math.max(bottom, bar.getBoundingClientRect().bottom);
    }
    if (bottom < 0) bottom = 0;
    bottom = Math.round(bottom);
    if (bottom === lastTop) return;
    lastTop = bottom;
    document.documentElement.style.setProperty('--kbw-sticky-top', bottom + 'px');
  }

  function loop() {
    readOffset();
    if (Date.now() < settleUntil) {
      requestAnimationFrame(loop);
    } else {
      looping = false;
    }
  }

  function kick() {
    settleUntil = Date.now() + SETTLE_MS;
    if (looping) return;
    looping = true;
    requestAnimationFrame(loop);
  }

  /* Only pages that draw a gallery column need it. */
  if (document.querySelector('.kbw-pdp__gallery')) {
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick, { passive: true });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', kick);
    } else {
      kick();
    }
  }
})();
